import type { IconType } from "react-icons";
import {
  FaBalanceScale,
  FaBoxOpen,
  FaClipboardCheck,
  FaClipboardList,
  FaFileAlt,
  FaGavel,
  FaInfoCircle,
  FaTags,
  FaUserCheck,
  FaUserPlus,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

export const WIZARD_STEPS = [
  { key: "info", icon: FaInfoCircle },
  { key: "classification", icon: FaTags },
  { key: "procedure", icon: FaGavel },
  { key: "qualification", icon: FaUserCheck },
  { key: "awardCriteria", icon: FaBalanceScale },
  { key: "minimumRequirements", icon: FaClipboardCheck },
  { key: "documents", icon: FaFileAlt },
  { key: "summary", icon: FaClipboardList },
  { key: "details", icon: FaClipboardList },
  { key: "products", icon: FaBoxOpen },
  { key: "invitations", icon: FaUserPlus },
  { key: "reviewAndPublish", icon: FaClipboardCheck },
] as const satisfies ReadonlyArray<{ key: string; icon: IconType }>;

export type PublicEventStep = (typeof WIZARD_STEPS)[number]["key"];

export const WIZARD_STEP_KEYS = WIZARD_STEPS.map((s) => s.key) as readonly PublicEventStep[];

export type WizardStep = (typeof WIZARD_STEPS)[number];

export interface ProcessDefinition {
  key: "publicOpen" | "private";
  steps: ReadonlyArray<WizardStep>;
}

export interface OrgWizardAccess {
  hasPublicProcurementAccess: boolean;
  hasProductAccess: boolean;
}

const step = (key: PublicEventStep): WizardStep => {
  const found = WIZARD_STEPS.find((s) => s.key === key);
  if (!found) throw new Error(`Unknown wizard step: ${key}`);
  return found;
};

const PUBLIC_OPEN: ProcessDefinition = {
  key: "publicOpen",
  steps: [
    "info",
    "classification",
    "procedure",
    "qualification",
    "awardCriteria",
    "minimumRequirements",
    "documents",
    "summary",
  ].map((key) => step(key as PublicEventStep)),
};

const privateProcess = (hasProductAccess: boolean): ProcessDefinition => ({
  key: "private",
  steps: (hasProductAccess
    ? ["info", "details", "products", "documents", "invitations", "reviewAndPublish"]
    : ["info", "details", "documents", "invitations", "reviewAndPublish"]
  ).map((key) => step(key as PublicEventStep)),
});

export const resolveProcess = (
  contextType: "Unclassified" | "Public" | "Private",
  access: OrgWizardAccess,
): ProcessDefinition | null => {
  if (contextType === "Public") return PUBLIC_OPEN;
  if (contextType === "Private") return privateProcess(access.hasProductAccess);
  return access.hasPublicProcurementAccess ? null : privateProcess(access.hasProductAccess);
};

export const useStepLabel = (): ((key: PublicEventStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "info":
        return t("Basic information");
      case "classification":
        return t("Classification and contract");
      case "procedure":
        return t("Procedure");
      case "qualification":
        return t("Qualification requirements");
      case "awardCriteria":
        return t("Award criteria");
      case "minimumRequirements":
        return t("Minimum requirements");
      case "documents":
        return t("Documents");
      case "summary":
        return t("Summary");
      case "details":
        return t("Details");
      case "products":
        return t("Products");
      case "invitations":
        return t("Invitations");
      case "reviewAndPublish":
        return t("Review and publish");
    }
  };
};

/** Short labels for the horizontal process bar (full labels are kept for buttons + the mobile drawer). */
export const useStepShortLabel = (): ((key: PublicEventStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "info":
        return t("Basics");
      case "classification":
        return t("Classification");
      case "procedure":
        return t("Procedure");
      case "qualification":
        return t("Qualification");
      case "awardCriteria":
        return t("Award criteria");
      case "minimumRequirements":
        return t("Minimum requirements");
      case "documents":
        return t("Documents");
      case "summary":
        return t("Summary");
      case "details":
        return t("Details");
      case "products":
        return t("Products");
      case "invitations":
        return t("Invitations");
      case "reviewAndPublish":
        return t("Publish");
    }
  };
};

export const stepNeighbors = (
  current: PublicEventStep,
  steps: ReadonlyArray<WizardStep>,
): { previous: PublicEventStep | undefined; next: PublicEventStep | undefined } => {
  const keys: readonly PublicEventStep[] = steps.map((s) => s.key);
  const idx = keys.indexOf(current);
  return {
    previous: idx > 0 ? keys[idx - 1] : undefined,
    next: idx >= 0 && idx < keys.length - 1 ? keys[idx + 1] : undefined,
  };
};
